import { Calendar, Globe, MapPin } from 'react-feather';
import styled, { css } from 'styled-components';
import uniq from 'lodash/uniq';

import { SportType } from '__generated__/graphql';

import { UIProps } from '../../theme/mixins';
import { assetUrl, getUrlWithoutPrefix } from '../helpers';
import DateRange from './DateRange';
import UI from '../UI';

interface EventHeaderProps {
  headerUrl?: File | string | null;
  alt?: string;
  sportType: SportType | null;
  color?: string;
  sc?: UIProps;
}

export const EventHeader = ({ headerUrl, sportType, alt, color, sc = {} }: EventHeaderProps) => {
  const defaultHeaderUrl = assetUrl(`images/default-event-headers/${sportType || SportType.other}.webp`);

  return (
    <EventHeaderContainer sc={{ borderRadius: 'xl', ...sc }} color={color}>
      <img
        src={headerUrl instanceof File
          ? URL.createObjectURL(headerUrl)
          : (headerUrl ?? defaultHeaderUrl)}
        alt={alt}
      />
      {!headerUrl && <div /> /* Gradient */}
    </EventHeaderContainer>
  );
};

export const EventHeaderContainer = styled(UI.Div)<UIProps & { color?: string; }>`
  ${({ sc: props = {}, color, theme }) => css`
    position: relative;
    overflow: hidden;
    aspect-ratio: 3.5;

    > img {
      object-fit: cover;
      object-position: center center;
      width: 100%;
      height: 100%;
      display: block;
      inset: 0;
      z-index: 1;

      ${props && theme.useBorder(props)}
    }

    > div {
      position: absolute;
      inset: 0;
      z-index: 2;
      background: linear-gradient(0deg, ${theme.transparentize(color ?? theme.colors.secondary[500], 0.5)} 0%, ${theme.transparentize(color ?? theme.colors.secondary[500], 0.8)} 60%, transparent 100%);
    }
  `}
`;

export interface EventInfoProps {
  event: {
    start_date: string;
    end_date: string;
    location?: {
      id?: string | null;
      description?: string | null;
      city?: string | null;
    } | null;
    website_url?: string | null;
  };
  color?: string;
  direction?: 'row' | 'column';
}

export const EventInfo = ({ event, direction = 'row', color = 'gray.600' }: EventInfoProps) => {
  const locationDescription = uniq([
    event.location?.description?.split(', ')[0], // For example: NDSM, NDSM-Plein, Amsterdam, Netherlands
    event.location?.city, // For example: Amsterdam
  ].filter((value) => value)).join(', '); // Result: NDSM, Amsterdam

  return (
    <UI.FlexContainer sc={{ direction, wrap: 'wrap', color }}>
      <UI.Span sc={{ mr: direction === 'row' ? 2 : undefined }}>
        <UI.Icon sc={{ mr: 1 }}>
          <Calendar />
        </UI.Icon>
        <UI.Span>
          <DateRange start={event.start_date} end={event.end_date} />
        </UI.Span>
      </UI.Span>

      {event.location.description && (
        <UI.Span sc={{ mr: direction === 'row' ? 2 : undefined }}>
          <UI.Icon sc={{ mr: 1 }}>
            <MapPin />
          </UI.Icon>
          {event.location.id && (
            <UI.A
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.location.description)}&query_place_id=${encodeURIComponent(event.location.id)}`}
              target="_blank"
              rel="noopener noreferrer"
              sc={{ color }}
            >
              {locationDescription}
            </UI.A>
          )}
          {!event.location.id && (
            <UI.Span>
              {locationDescription}
            </UI.Span>
          )}
        </UI.Span>
      )}

      {event.website_url && (
        <UI.Span>
          <UI.Icon sc={{ mr: 1 }}>
            <Globe />
          </UI.Icon>
          <UI.A href={event.website_url} target="_blank" rel="noopener noreferrer" sc={{ color }}>
            {getUrlWithoutPrefix(event.website_url).split('/')[0]}
          </UI.A>
        </UI.Span>
      )}
    </UI.FlexContainer>
  );
};
